import {t} from "i18next";
import {
  LitElement,
  nothing,
  type PropertyValues,
  type TemplateResult
} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {html, unsafeStatic} from "lit/static-html.js";
import ChevronDown from "@interstate/icons-svg/chevron-down.svg?raw";
import ChevronUp from "@interstate/icons-svg/chevron-up.svg?raw";

import {ROOT_TAG} from "../../globals";
import {InterstateIcon} from "../../interstate";
import type {SideNavItemLayout} from "./SideNavItemElement";

export const tagName = `${ROOT_TAG}-side-nav-scroller`;
export const SideNavScroller = unsafeStatic(tagName);

const SCROLLER_ID = `${ROOT_TAG}-side-nav-scroller`;
const SCROLLER_TESTID = "side-nav-scroller";
const OVERFLOW_TOGGLE_TESTID = "side-nav-overflow-toggle";
const END_DIVIDER_TESTID = "side-nav-end-divider";

const OVERFLOW_TOLERANCE_PX = 1;
// Fractional zoom and pixel ratios leave the true scroll end up to ~1.1px short of scrollHeight.
const AT_END_TOLERANCE_PX = 2;

const SCROLLER_CONTENT_CLASSES: Record<SideNavItemLayout, string> = {
  "overlay": "flex flex-col [&>*]:shrink-0 px-1.5 pt-5",
  "rail": "flex flex-col [&>*]:shrink-0 px-1.5 pt-5",
  "mobile-menu": "flex flex-col [&>*]:shrink-0 px-1.5"
};

/**
 * Wraps arbitrary row content in a scrollable region with an overflow chevron:
 * the chevron appears only when the content overflows, toggles scrolling on/off,
 * and flips direction once scrolled to the end. Shared verbatim across the fixed
 * rail, the float overlay and the mobile Menu.
 */
@customElement(tagName)
export class SideNavScrollerElement extends LitElement {
  @property({attribute: false})
  content?: TemplateResult;

  @property()
  layout: SideNavItemLayout = "overlay";

  /** Applied to the content wrapper (not the chevron), so a `role="menu"` owner only ever sees `menuitem`/`group`/`separator` children. */
  @property()
  contentRole?: string;

  /** Whether the content can include a trailing divider once scrolling is on (i.e. solutions exist). */
  @property({type: Boolean, attribute: false})
  showEndDivider = false;

  /** Fixed rail: the chevron is always visible, so it stays in the tab order unconditionally. */
  @property({type: Boolean, attribute: false})
  alwaysTabbable = false;

  /** Mirrors the parent surface's open state: gates the chevron's tab order and resets scroll on reopen. */
  @property({type: Boolean, attribute: false})
  isOpen = false;

  @state()
  private hasOverflow = false;

  @state()
  private scrollEnabled = false;

  @state()
  private isScrollToBottom = false;

  @state()
  private thumbTopPercent = 0;

  @state()
  private thumbHeightPercent = 100;

  @query(`[data-testid="${SCROLLER_TESTID}"]`)
  private scrollerEl?: HTMLElement;

  private resizeObserver?: ResizeObserver;
  private observedScroller?: Element;

  private handleScrollerScroll = () => {
    const region = this.observedScroller;
    if (!region) return;
    this.isScrollToBottom =
      region.scrollHeight - region.scrollTop - region.clientHeight <=
      AT_END_TOLERANCE_PX;
    this.updateThumbMetrics();
  };

  /** Light DOM, so the side nav's stylesheet reaches this region and its rows. */
  protected override createRenderRoot() {
    return this;
  }

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("isOpen") && this.isOpen) {
      this.scrollEnabled = false;
      this.isScrollToBottom = false;
      if (this.observedScroller) {
        this.observedScroller.scrollTop = 0;
      }
    }
  }

  override updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    this.syncScrollerObservers();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardownScrollerObservers();
  }

  private syncScrollerObservers(): void {
    const region = this.scrollerEl;
    if (region !== this.observedScroller) {
      this.teardownScrollerObservers();
      this.observedScroller = region ?? undefined;
      if (region) {
        region.addEventListener("scroll", this.handleScrollerScroll, {
          passive: true
        });
        this.resizeObserver = new ResizeObserver(() => this.measureOverflow());
        this.resizeObserver.observe(region);
        if (region.firstElementChild) {
          this.resizeObserver.observe(region.firstElementChild);
        }
      }
    }
    if (!region) {
      this.hasOverflow = false;
      return;
    }
    this.measureOverflow();
  }

  private measureOverflow(): void {
    const region = this.observedScroller;
    if (!region) {
      this.hasOverflow = false;
      return;
    }
    this.hasOverflow =
      region.scrollHeight - region.clientHeight > OVERFLOW_TOLERANCE_PX;
    if (!this.hasOverflow && this.scrollEnabled) {
      // Overflow is gone, so no chevron remains to undo the scroll bar and divider.
      this.scrollEnabled = false;
      this.isScrollToBottom = false;
      region.scrollTop = 0;
    }
    if (this.scrollEnabled) {
      this.updateThumbMetrics();
    }
  }

  private updateThumbMetrics(): void {
    const region = this.observedScroller;
    if (!region || region.scrollHeight <= 0) {
      this.thumbTopPercent = 0;
      this.thumbHeightPercent = 100;
      return;
    }
    this.thumbHeightPercent = Math.min(
      100,
      (region.clientHeight / region.scrollHeight) * 100
    );
    const scrollableDistance = region.scrollHeight - region.clientHeight;
    const scrollRatio =
      scrollableDistance > 0 ? region.scrollTop / scrollableDistance : 0;
    this.thumbTopPercent = (100 - this.thumbHeightPercent) * scrollRatio;
  }

  private teardownScrollerObservers(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.observedScroller?.removeEventListener(
      "scroll",
      this.handleScrollerScroll
    );
    this.observedScroller = undefined;
  }

  private toggleScroll(): void {
    if (!this.scrollEnabled) {
      this.scrollEnabled = true;
      this.isScrollToBottom = false;
      this.updateThumbMetrics();
      return;
    }
    this.scrollEnabled = false;
    this.isScrollToBottom = false;
    if (this.observedScroller) {
      this.observedScroller.scrollTop = 0;
    }
  }

  private chevronDirection(): "up" | "down" {
    return this.scrollEnabled && this.isScrollToBottom ? "up" : "down";
  }

  private renderEndDivider() {
    return html`<div
      data-testid=${END_DIVIDER_TESTID}
      role="separator"
      class="mt-2 mx-2 flex-shrink-0 border-t border-solid border-neutral-200 dark:border-primary-700"></div>`;
  }

  private renderOverflowSeparator() {
    if (!this.hasOverflow) {
      return nothing;
    }
    return html`<div
      aria-hidden="true"
      class="h-px w-full shrink-0 bg-[rgba(0,0,0,0.12)] shadow-[0px_-2px_4px_0px_rgba(0,0,0,0.3)] dark:bg-white/10 dark:shadow-[0px_-2px_4px_0px_rgba(255,255,255,0.15)]"></div>`;
  }

  private renderOverflowToggle() {
    if (!this.hasOverflow) {
      return nothing;
    }
    const direction = this.chevronDirection();
    const glyph = direction === "up" ? ChevronUp : ChevronDown;
    // Tied to scrollEnabled (the actual effect of toggleScroll()), not `direction`,
    // so the label never promises "show more" when the click is about to scroll to top.
    const label = this.scrollEnabled
      ? t("sideNav.chevronBackToTop", {defaultValue: "Back to top"})
      : t("sideNav.chevronShowMore", {
          defaultValue: "Show more navigation items"
        });
    return html`
      <button
        data-testid=${OVERFLOW_TOGGLE_TESTID}
        data-direction=${direction}
        type="button"
        tabindex=${this.isOpen || this.alwaysTabbable ? nothing : "-1"}
        aria-controls=${SCROLLER_ID}
        class="my-2 flex h-8 w-8 flex-shrink-0 items-center justify-center self-center
          rounded-full border-0 bg-white p-0 text-primary-700 shadow-md hover:bg-neutral-100
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-700
          dark:bg-primary-700 dark:text-white dark:hover:bg-primary-600"
        aria-expanded=${this.scrollEnabled ? "true" : "false"}
        aria-label=${label}
        @click=${() => this.toggleScroll()}>
        <${InterstateIcon} svg="${glyph}" class="flex-shrink-0 text-base"></${InterstateIcon}>
      </button>
    `;
  }

  private renderCustomScrollbar() {
    if (!this.hasOverflow || !this.scrollEnabled) {
      return nothing;
    }
    return html`<div
      aria-hidden="true"
      class="pointer-events-none absolute bottom-0 right-0 top-0 w-1.5">
      <div
        class="absolute left-0 w-full rounded-full bg-neutral-300 dark:bg-primary-600"
        style="top:${this.thumbTopPercent}%; height:${this
          .thumbHeightPercent}%"></div>
    </div>`;
  }

  override render() {
    return html`
      <div class="relative flex flex-1 min-h-0 flex-col">
        <div
          data-testid=${SCROLLER_TESTID}
          id=${SCROLLER_ID}
          class=${classMap({
            "min-h-0": true,
            "flex-1": true,
            "[scrollbar-width:none]": true,
            "[&::-webkit-scrollbar]:hidden": true,
            "overflow-y-auto": this.scrollEnabled,
            "overflow-hidden": !this.scrollEnabled
          })}>
          <div
            role=${this.contentRole ?? nothing}
            class=${SCROLLER_CONTENT_CLASSES[this.layout]}>
            ${this.content}
            ${this.showEndDivider && this.scrollEnabled
              ? this.renderEndDivider()
              : nothing}
          </div>
        </div>
        ${this.renderCustomScrollbar()}
      </div>
      ${this.renderOverflowSeparator()} ${this.renderOverflowToggle()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: SideNavScrollerElement;
  }
}
